import * as DialogPrimitive from '@radix-ui/react-dialog'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import * as TooltipPrimitive from '@radix-ui/react-tooltip'
import { Check, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { cn } from './lib/cn'
import { Button, Kbd } from './primitives'
import { Input } from './forms'

/* ==========================================================================
 * Modal
 * ======================================================================== */

const OVERLAY_CLASS = [
  'fixed inset-0 z-50 bg-overlay/40 backdrop-blur-sm',
  'data-[state=open]:animate-in data-[state=open]:fade-in-0',
  'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
].join(' ')

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl'

const MODAL_WIDTH: Record<ModalSize, string> = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-lg',
  lg: 'sm:max-w-2xl',
  xl: 'sm:max-w-4xl',
}

export interface ModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: React.ReactNode
  description?: React.ReactNode
  /** Right-aligned action row. */
  footer?: React.ReactNode
  size?: ModalSize
  /** Set false for flows that must force an explicit choice (e.g. key reveal). */
  dismissible?: boolean
  className?: string
  children?: React.ReactNode
}

/**
 * Centred dialog: focus is trapped, Escape closes, the background is inert.
 * Below `sm` it becomes a bottom sheet, which is far easier to reach on a
 * phone than a centred card.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  footer,
  size = 'md',
  dismissible = true,
  className,
  children,
}: ModalProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={OVERLAY_CLASS} />
        <DialogPrimitive.Content
          onEscapeKeyDown={(event) => {
            if (!dismissible) event.preventDefault()
          }}
          onPointerDownOutside={(event) => {
            if (!dismissible) event.preventDefault()
          }}
          className={cn(
            'fixed z-50 flex flex-col border border-line bg-raised shadow-overlay',
            // Bottom sheet on small screens
            'inset-x-0 bottom-0 max-h-[90dvh] rounded-t-card',
            'data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom',
            // Centred dialog from sm up
            'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-card',
            'sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95',
            MODAL_WIDTH[size],
            className
          )}
        >
          {title || description ? (
            <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
              <div className="space-y-1">
                <DialogPrimitive.Title className="text-sm font-semibold text-foreground">
                  {title}
                </DialogPrimitive.Title>
                {description ? (
                  <DialogPrimitive.Description className="text-caption text-muted">
                    {description}
                  </DialogPrimitive.Description>
                ) : null}
              </div>
              {dismissible ? (
                <DialogPrimitive.Close
                  aria-label="Close"
                  className="rounded-lg p-1.5 text-subtle transition-colors hover:bg-sunken hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </DialogPrimitive.Close>
              ) : null}
            </header>
          ) : (
            // Radix requires a title for screen readers even when it is not shown.
            <DialogPrimitive.Title className="sr-only">{title ?? 'Dialog'}</DialogPrimitive.Title>
          )}

          <div className="ms-scroll flex-1 overflow-y-auto p-5">{children}</div>

          {footer ? (
            <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
              {footer}
            </footer>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/* ==========================================================================
 * Sheet — slide-over and drawer
 * ======================================================================== */

export interface SheetProps extends Omit<ModalProps, 'size'> {
  side?: 'right' | 'left' | 'bottom'
}

const SHEET_POSITION: Record<NonNullable<SheetProps['side']>, string> = {
  right:
    'inset-y-0 right-0 h-full w-full sm:max-w-md data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
  left: 'inset-y-0 left-0 h-full w-full sm:max-w-md data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
  bottom:
    'inset-x-0 bottom-0 max-h-[85dvh] rounded-t-card data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
}

/** A panel that slides in from an edge. */
export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  footer,
  side = 'right',
  dismissible = true,
  className,
  children,
}: SheetProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={OVERLAY_CLASS} />
        <DialogPrimitive.Content
          onEscapeKeyDown={(event) => !dismissible && event.preventDefault()}
          onPointerDownOutside={(event) => !dismissible && event.preventDefault()}
          className={cn(
            'fixed z-50 flex flex-col border border-line bg-raised shadow-overlay',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            SHEET_POSITION[side],
            className
          )}
        >
          <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
            <div className="space-y-1">
              <DialogPrimitive.Title className="text-sm font-semibold text-foreground">
                {title}
              </DialogPrimitive.Title>
              {description ? (
                <DialogPrimitive.Description className="text-caption text-muted">
                  {description}
                </DialogPrimitive.Description>
              ) : null}
            </div>
            {dismissible ? (
              <DialogPrimitive.Close
                aria-label="Close"
                className="rounded-lg p-1.5 text-subtle transition-colors hover:bg-sunken hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </DialogPrimitive.Close>
            ) : null}
          </header>

          <div className="ms-scroll flex-1 overflow-y-auto p-5">{children}</div>

          {footer ? (
            <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
              {footer}
            </footer>
          ) : null}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

/** Convenience wrapper for the common right-hand detail panel. */
export function SlideOver(props: SheetProps) {
  return <Sheet side="right" {...props} />
}

/** Bottom sheet, for mobile-first flows. */
export function Drawer(props: SheetProps) {
  return <Sheet side="bottom" {...props} />
}

/* ==========================================================================
 * Tooltip
 * ======================================================================== */

export interface TooltipProps {
  content: React.ReactNode
  children: React.ReactNode
  side?: 'top' | 'right' | 'bottom' | 'left'
  className?: string
}

/**
 * Includes its own Provider, so a tooltip cannot silently fail to render
 * because a provider was forgotten further up the tree.
 */
export function Tooltip({ content, children, side = 'top', className }: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={250}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            className={cn(
              'z-50 max-w-xs rounded-lg border border-line bg-raised px-2.5 py-1.5',
              'text-caption text-foreground shadow-raised',
              'data-[state=delayed-open]:animate-in data-[state=delayed-open]:fade-in-0 data-[state=delayed-open]:zoom-in-95',
              className
            )}
          >
            {content}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  )
}

/* ==========================================================================
 * DropdownMenu
 * ======================================================================== */

export interface MenuItem {
  id: string
  label: React.ReactNode
  icon?: React.ReactNode
  onSelect?: () => void
  /** Renders in the destructive colour. */
  danger?: boolean
  disabled?: boolean
  /** Draws a separator above this item. */
  separated?: boolean
  /** Shows a check — for menus that toggle something. */
  checked?: boolean
}

export interface DropdownMenuProps {
  trigger: React.ReactNode
  items: MenuItem[]
  align?: 'start' | 'center' | 'end'
  /** Rendered at the top of the menu — usually the signed-in identity. */
  header?: React.ReactNode
  className?: string
}

export function DropdownMenu({
  trigger,
  items,
  align = 'end',
  header,
  className,
}: DropdownMenuProps) {
  return (
    <DropdownMenuPrimitive.Root>
      <DropdownMenuPrimitive.Trigger asChild>{trigger}</DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
          align={align}
          sideOffset={8}
          className={cn(
            'z-50 min-w-56 overflow-hidden rounded-card border border-line bg-raised p-1 shadow-overlay',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
            className
          )}
        >
          {header ? (
            <div className="border-b border-line px-3 py-2.5">{header}</div>
          ) : null}

          {items.map((item) => (
            <div key={item.id}>
              {item.separated ? <div className="my-1 h-px bg-line" /> : null}
              <DropdownMenuPrimitive.Item
                disabled={item.disabled}
                onSelect={item.onSelect}
                className={cn(
                  'relative flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-3 py-2 text-sm outline-none',
                  'transition-colors',
                  item.danger
                    ? 'text-danger-text data-[highlighted]:bg-danger-soft'
                    : 'text-foreground data-[highlighted]:bg-sunken',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50'
                )}
              >
                {item.icon ? (
                  <span className="shrink-0 text-subtle [&_svg]:h-4 [&_svg]:w-4">{item.icon}</span>
                ) : null}
                <span className="flex-1">{item.label}</span>
                {item.checked ? <Check className="h-4 w-4 text-accent" /> : null}
              </DropdownMenuPrimitive.Item>
            </div>
          ))}
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  )
}

/* ==========================================================================
 * Popover
 * ======================================================================== */

export interface PopoverProps {
  trigger: React.ReactNode
  children: React.ReactNode
  align?: 'start' | 'center' | 'end'
  className?: string
}

export function Popover({ trigger, children, align = 'end', className }: PopoverProps) {
  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={align}
          sideOffset={8}
          className={cn(
            'z-50 w-80 rounded-card border border-line bg-raised p-4 shadow-overlay',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            className
          )}
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}

/* ==========================================================================
 * ConfirmDialog
 * ======================================================================== */

export interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Use the destructive button styling. */
  destructive?: boolean
  onConfirm: () => void
  loading?: boolean
  children?: React.ReactNode
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  loading = false,
  children,
}: ConfirmDialogProps) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  )
}

export interface TypedConfirmDialogProps
  extends Omit<ConfirmDialogProps, 'onConfirm' | 'children'> {
  /** The exact string the user must type, e.g. the resource name. */
  confirmationText: string
  onConfirm: () => void
  /** Explains what will be destroyed. */
  children?: React.ReactNode
}

/**
 * Destructive confirmation gated behind typing a resource name, so a delete
 * cannot be triggered by a stray double-click.
 */
export function TypedConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  destructive = true,
  confirmationText,
  onConfirm,
  loading = false,
  children,
}: TypedConfirmDialogProps) {
  const [typed, setTyped] = useState('')
  const matches = typed === confirmationText

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) setTyped('')
        onOpenChange(next)
      }}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            disabled={!matches}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {children}
        <div className="space-y-1.5">
          <p className="text-caption text-muted">
            Type <code className="rounded bg-sunken px-1.5 py-0.5 font-mono text-foreground">{confirmationText}</code> to
            confirm.
          </p>
          <Input
            value={typed}
            onChange={(event) => setTyped(event.currentTarget.value)}
            placeholder={confirmationText}
            autoComplete="off"
            autoFocus
          />
        </div>
      </div>
    </Modal>
  )
}

/* ==========================================================================
 * CommandPalette
 * ======================================================================== */

export interface CommandItem {
  id: string
  label: string
  /** Group heading, e.g. "Navigate". */
  group?: string
  icon?: React.ReactNode
  hint?: React.ReactNode
  onSelect: () => void
}

export interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  items: CommandItem[]
  placeholder?: string
  emptyMessage?: string
}

/**
 * Keyboard-first navigation. Arrow keys move, Enter runs, Escape closes.
 * Filtering is local, so it stays instant without a round trip.
 */
export function CommandPalette({
  open,
  onOpenChange,
  items,
  placeholder = 'Search…',
  emptyMessage = 'No matches',
}: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return items
    return items.filter((item) => item.label.toLowerCase().includes(q))
  }, [items, query])

  const grouped = useMemo(() => {
    const map = new Map<string, CommandItem[]>()
    for (const item of filtered) {
      const group = item.group ?? ''
      const bucket = map.get(group)
      if (bucket) bucket.push(item)
      else map.set(group, [item])
    }
    return [...map.entries()]
  }, [filtered])

  const close = () => {
    setQuery('')
    setActiveIndex(0)
    onOpenChange(false)
  }

  const run = (item: CommandItem) => {
    item.onSelect()
    close()
  }

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close()
        else onOpenChange(true)
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={OVERLAY_CLASS} />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-[12vh] z-50 w-[calc(100%-2rem)] max-w-xl -translate-x-1/2',
            'overflow-hidden rounded-card border border-line bg-raised shadow-overlay',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
          )}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>

          <div className="flex items-center gap-2 border-b border-line px-3">
            <Search className="h-4 w-4 shrink-0 text-subtle" />
            <input
              autoFocus
              value={query}
              placeholder={placeholder}
              onChange={(event) => {
                setQuery(event.currentTarget.value)
                setActiveIndex(0)
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault()
                  setActiveIndex((i) => Math.min(i + 1, filtered.length - 1))
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault()
                  setActiveIndex((i) => Math.max(i - 1, 0))
                } else if (event.key === 'Enter') {
                  event.preventDefault()
                  const item = filtered[activeIndex]
                  if (item) run(item)
                }
              }}
              className="h-12 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-subtle"
            />
            <Kbd className="hidden sm:inline-flex">Esc</Kbd>
          </div>

          <div className="ms-scroll max-h-80 overflow-y-auto p-1.5">
            {filtered.length === 0 ? (
              <p className="px-3 py-6 text-center text-caption text-muted">{emptyMessage}</p>
            ) : (
              grouped.map(([group, groupItems]) => (
                <div key={group} className="mb-1 last:mb-0">
                  {group ? (
                    <p className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-widest text-subtle">
                      {group}
                    </p>
                  ) : null}
                  {groupItems.map((item) => {
                    const index = filtered.indexOf(item)
                    const active = index === activeIndex

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => run(item)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors',
                          active ? 'bg-sunken text-foreground' : 'text-muted'
                        )}
                      >
                        {item.icon ? (
                          <span className="shrink-0 text-subtle [&_svg]:h-4 [&_svg]:w-4">
                            {item.icon}
                          </span>
                        ) : null}
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.hint ? (
                          <span className="shrink-0 text-caption text-subtle">{item.hint}</span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              ))
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
